"use client";

/**
 * The developer site heals a stale tab. Staging pins no deployment (apps/web/deploy.sh sets deploymentId only on
 * production). Production pins one, but Vercel serves a pinned request from a deployment only for the project's
 * Skew Protection max age counted from that deployment's creation (12 hours here), and production deploys land days
 * apart: on the 2026-10-04 promote the build before had aged out five days earlier, and its own stylesheet, asked for
 * with its own ?dpl= pin, 404'd on vestra.ai. So on both, once a deploy repoints the alias, an open tab's old build
 * is gone from it. Next itself hard-navigates when a fresh page comes back from another build; what breaks is a page
 * the old router serves from its own prefetch cache (next.config staleTimes.static, 30 minutes): that payload names
 * the old build's chunks and stylesheets, they 404, and the page paints unstyled (monospace pale prose, an install
 * line split into spaced tokens, a lone copy button). A fresh load is always fine, so the cure is a fresh load.
 *
 * Known stale, before the click: cheap reads of the /api/version beacon (its commitSha against the sha this bundle
 * was built with) that only MARK the tab: on a return to the tab, on hovering or focusing a link (at most one read a
 * minute), and every five minutes while in view (a tab on a second screen never fires visibilitychange). A marked
 * tab turns a plain click on a /developers link into a full load of that page, so the old router never renders it.
 *
 * Healed after the fact, with one hard reload: a navigation that finds the build changed, the tab going hidden while
 * stale (unseen), a failed chunk or stylesheet of our own, and a page that has painted unstyled, but only once the
 * beacon confirms a new build, so an offline blip never reloads. Unstyled is the net under every path, whatever the
 * host: once the document has loaded, a stylesheet of ours with no sheet (a client navigation into /developers
 * commits after its stylesheets settle, so that error fired before this heal mounted to hear it), or a family root
 * missing the mark its own stylesheet sets (the sheet never reached the page at all). A change found while the page
 * is in view never reloads it under its reader, unless the page has painted unstyled: that page is already broken.
 *
 * Guarded: one reload per built sha per tab (sessionStorage); no storage, no reload. Every "can't tell" (no built
 * sha in local dev, an unreachable beacon) does nothing, and a read still in flight when the heal unmounts (the
 * console, outside this layout, shows a key's secret once) is aborted and acts on nothing. Nothing is shown. The
 * app's own heal (lib/buildFreshness.ts) is not imported: it reads the app's stores.
 */
import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";

const CLAIM_KEY = "vestra:developers-reload";
/** At most one marking read of the beacon a minute: hovers and returns come in bursts. */
const MARK_EVERY_MS = 60_000;
/** A tab in view on a second screen never fires visibilitychange: it marks on this clock instead. */
const POLL_MS = 5 * 60_000;

/** The sha compiled into this bundle; empty in local dev, where none is set. */
function builtSha(): string {
  const sha = process.env.BUILD_COMMIT_SHA || "";
  return sha === "unknown" ? "" : sha;
}

/** The read's own timeout, ended early too when `life` ends. */
function within(life: AbortSignal | undefined, ms: number): AbortSignal {
  const timeout = AbortSignal.timeout(ms);
  return life && typeof AbortSignal.any === "function"
    ? AbortSignal.any([life, timeout])
    : timeout;
}

/** True only when the beacon proves this bundle stale; false whenever it cannot tell, or `life` has ended. */
export async function buildChanged(
  fetchImpl: typeof fetch = fetch,
  life?: AbortSignal
): Promise<boolean> {
  const ours = builtSha();
  if (!ours || life?.aborted) return false;
  try {
    // no-store and a cache buster: a cached beacon from this tab's own build would say nothing ever changed.
    const res = await fetchImpl(`/api/version?cb=${Date.now()}`, {
      cache: "no-store",
      signal: within(life, 3000),
    });
    if (!res.ok) return false;
    const serving = ((await res.json()) as { commitSha?: unknown } | null)
      ?.commitSha;
    return (
      !life?.aborted &&
      typeof serving === "string" &&
      serving !== "" &&
      serving !== "unknown" &&
      serving !== ours
    );
  } catch {
    return false;
  }
}

/** One reload per built sha per tab: false once spent, and false when storage cannot hold the claim. */
export function claimReload(): boolean {
  const sha = builtSha();
  if (!sha) return false;
  try {
    const key = `${CLAIM_KEY}:${sha}`;
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

/** A script or stylesheet of this build's own (under /_next/static/ on this origin), never a third party's. */
function ownAsset(url: string): boolean {
  try {
    const u = new URL(url, window.location.href);
    return (
      u.origin === window.location.origin &&
      u.pathname.startsWith("/_next/static/")
    );
  } catch {
    return false;
  }
}

/** The bundler's error for a chunk that would not load: webpack names it ChunkLoadError, Turbopack says so in words. */
function chunkLoadError(err: unknown): boolean {
  return (
    err instanceof Error &&
    (err.name === "ChunkLoadError" ||
      err.message.startsWith("Failed to load chunk "))
  );
}

/** A load that failed: our stylesheet or script element, or a chunk error thrown or rejected. */
export function failedLoad(e: Event): boolean {
  const el = e.target;
  if (el instanceof HTMLLinkElement)
    return el.rel.split(/\s+/).includes("stylesheet") && ownAsset(el.href);
  if (el instanceof HTMLScriptElement) return el.src !== "" && ownAsset(el.src);
  if (e instanceof ErrorEvent) return chunkLoadError(e.error);
  return chunkLoadError((e as PromiseRejectionEvent).reason);
}

/** Each family root, and the custom property its own stylesheet sets on it (_family/family.css, _docs/docs.css). */
export const SHEET_MARKS: ReadonlyArray<readonly [root: string, mark: string]> =
  [
    [".vfam", "--vfam-sheet"],
    [".vdocs", "--vdocs-sheet"],
  ];

/**
 * The page painted unstyled, judged once its stylesheets have settled: a stylesheet of our own with no sheet (it
 * 404'd), or a family root its stylesheet's mark never reached (the sheet is not in the page, however it went missing).
 */
export function unstyled(): boolean {
  const failed = Array.from(
    document.querySelectorAll<HTMLLinkElement>('link[rel~="stylesheet"]')
  ).some((link) => !link.sheet && ownAsset(link.href));
  return (
    failed ||
    SHEET_MARKS.some(([root, mark]) =>
      Array.from(document.querySelectorAll(root)).some(
        (el) => window.getComputedStyle(el).getPropertyValue(mark).trim() === ""
      )
    )
  );
}

/** The /developers page a link opens in this tab, or null: another site, a new tab, a download, a move on this page. */
export function siteLink(target: EventTarget | null): string | null {
  const a = target instanceof Element ? target.closest("a[href]") : null;
  if (!(a instanceof HTMLAnchorElement)) return null;
  if ((a.target && a.target !== "_self") || a.hasAttribute("download"))
    return null;
  try {
    const url = new URL(a.href, window.location.href);
    const here = window.location;
    if (url.origin !== here.origin) return null;
    if (
      url.pathname !== "/developers" &&
      !url.pathname.startsWith("/developers/")
    )
      return null;
    if (url.pathname === here.pathname && url.search === here.search)
      return null;
    return url.href;
  } catch {
    return null;
  }
}

/** The one automatic reload, when the claim allows it. */
function healWith(reload: () => void): void {
  if (claimReload()) reload();
}

/** Seams for tests: jsdom cannot load a page. */
function hardReload(): void {
  window.location.reload();
}
function hardAssign(href: string): void {
  window.location.assign(href);
}

export function StaleTabHeal({
  fetchImpl = fetch,
  reload = hardReload,
  assign = hardAssign,
}: {
  fetchImpl?: typeof fetch;
  reload?: () => void;
  assign?: (href: string) => void;
}) {
  const pathname = usePathname();
  const shown = useRef<string | null>(null);
  // The beacon proved this build stale: every leg acts on it without another read.
  const stale = useRef(false);
  // What a client navigation does, set while the heal is mounted.
  const navigated = useRef<(() => void) | null>(null);

  useEffect(() => {
    const life = new AbortController();
    let lastMark = 0;
    const hidden = () => document.visibilityState === "hidden";
    const heal = () => {
      if (!life.signal.aborted) healWith(reload);
    };
    /** True when the build changed (the tab is then marked stale); read only while not already known. */
    const confirm = async (): Promise<boolean> => {
      if (stale.current) return !life.signal.aborted;
      if (!(await buildChanged(fetchImpl, life.signal))) return false;
      stale.current = true;
      return !life.signal.aborted;
    };
    /** Marks, at most one read a minute; reloads only a page that has painted unstyled, already broken for its reader. */
    const mark = () => {
      if (stale.current || Date.now() - lastMark < MARK_EVERY_MS) return;
      lastMark = Date.now();
      void confirm().then((changed) => {
        if (changed && unstyled()) heal();
      });
    };

    /** The one reload, once the beacon proves the build changed. */
    const healIfChanged = () => {
      void confirm().then((changed) => {
        if (changed) heal();
      });
    };

    navigated.current = healIfChanged;
    // Judged once the document has loaded, when the page's own stylesheets have settled.
    const onArrival = () => {
      if (unstyled()) healIfChanged();
    };
    const onVisibility = () => {
      if (!hidden()) return mark();
      void confirm().then((changed) => {
        if (changed && hidden()) heal();
      });
    };
    const onFailedLoad = (e: Event) => {
      if (failedLoad(e)) healIfChanged();
    };
    const onPointLink = (e: Event) => {
      if (siteLink(e.target)) mark();
    };
    const onClick = (e: MouseEvent) => {
      if (!stale.current || e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const href = siteLink(e.target);
      if (!href || !claimReload()) return;
      // Before next/link's own handler, which leaves a prevented click alone: the old router never renders it.
      e.preventDefault();
      assign(href);
    };
    const poll = setInterval(() => {
      if (!hidden()) mark();
    }, POLL_MS);

    document.addEventListener("visibilitychange", onVisibility);
    // Capture: a stylesheet's or script's error does not bubble, and the click must come before the router's.
    window.addEventListener("error", onFailedLoad, true);
    window.addEventListener("unhandledrejection", onFailedLoad);
    document.addEventListener("click", onClick, true);
    document.addEventListener("pointerover", onPointLink, true);
    document.addEventListener("focusin", onPointLink, true);
    if (document.readyState === "complete") onArrival();
    else window.addEventListener("load", onArrival);
    return () => {
      life.abort();
      navigated.current = null;
      clearInterval(poll);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("error", onFailedLoad, true);
      window.removeEventListener("unhandledrejection", onFailedLoad);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("pointerover", onPointLink, true);
      document.removeEventListener("focusin", onPointLink, true);
      window.removeEventListener("load", onArrival);
    };
  }, [fetchImpl, reload, assign]);

  useEffect(() => {
    // The first path is the page as loaded: a fresh load is the current build.
    if (shown.current === null || shown.current === pathname) {
      shown.current = pathname;
      return;
    }
    shown.current = pathname;
    navigated.current?.();
  }, [pathname]);

  return null;
}
